import { useM2 } from '@/hooks/useReportes';
import { cn } from '@/lib/utils';
import type { M2Partida, M2Reporte } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, MagnifyingGlass } from '@phosphor-icons/react';
import { type CSSProperties, useMemo, useState } from 'react';

type Vista = 'partida' | 'matriz';
type Metrica = 'semana' | 'acumulado';

const EPS = 0.001;

/** Formatea m² con 1 decimal (o "—" si no aplica). */
function fmt(n: number | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Obras que aportaron a una sub-partida (desde el mapa de celdas). */
function obrasDeSub(m: M2Reporte, subId: number, soloAvance: boolean) {
  return m.obras
    .map((o) => ({ o, c: m.celdas[`${o.codigo}|${subId}`] }))
    .filter((x) => x.c != null && (soloAvance ? x.c.s > EPS : x.c.a > EPS || x.c.s > EPS))
    .sort((a, b) => a.o.codigo.localeCompare(b.o.codigo));
}

/**
 * Reporte de m² producidos por partida / sub-partida (semana + acumulado).
 * Descompone el KPI "M² construidos semana" al nivel de cada sub-partida.
 * Dos vistas: Por Partida (con desglose por obra) y Matriz (sub × obra).
 */
export function M2Vista({ semana }: { semana: number | null }) {
  const { data, isLoading, isError } = useM2(semana);
  const m = data?.data;
  const [vista, setVista] = useState<Vista>('partida');
  const [metrica, setMetrica] = useState<Metrica>('semana');
  const [soloAvance, setSoloAvance] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando m²…
      </div>
    );
  }
  if (isError || !m) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No se pudo cargar el reporte.
      </p>
    );
  }

  return (
    <div className="relative left-1/2 w-screen -translate-x-1/2 px-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {/* Toggle de vista */}
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(
            [
              ['partida', 'Por Partida'],
              ['matriz', 'Matriz por obra'],
            ] as [Vista, string][]
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                vista === v
                  ? 'bg-adelante-primary text-adelante-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Toggle de métrica (solo relevante para la matriz; en la tabla se ven ambas) */}
        {vista === 'matriz' && (
          <div className="inline-flex rounded-md border border-border p-0.5">
            {(
              [
                ['semana', 'Semana'],
                ['acumulado', 'Acumulado'],
              ] as [Metrica, string][]
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setMetrica(v)}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors',
                  metrica === v
                    ? 'bg-adelante-primary text-adelante-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Filtro: solo obras/sub-partidas con avance esta semana */}
        <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs">
          <input
            type="checkbox"
            checked={soloAvance}
            onChange={(e) => setSoloAvance(e.target.checked)}
          />
          Solo con avance
        </label>

        {vista === 'matriz' && (
          <div className="relative">
            <MagnifyingGlass
              className="-translate-y-1/2 absolute top-1/2 left-2 h-4 w-4 text-muted-foreground"
              weight="bold"
              aria-hidden="true"
            />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar obra…"
              className="h-8 w-56 rounded-md border border-border pr-2 pl-8 text-sm"
            />
          </div>
        )}

        <span className="text-xs text-muted-foreground">
          Total semana <strong className="text-foreground">{fmt(m.total_semana)}</strong> m² ·
          acumulado <strong className="text-foreground">{fmt(m.total_acumulado)}</strong> m² ·{' '}
          {m.cerrada ? 'foto del cierre' : 'estado vivo (semana abierta)'}
        </span>
      </div>

      {!m.base_semanal && (
        <p className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
          ⚠️ Esta semana no tiene línea base ni cierre previo: el m² de la semana no es fiable (no
          hay contra qué medir el avance efectivo). El acumulado sí es válido.
        </p>
      )}

      {vista === 'partida' ? (
        <M2PorPartida m={m} soloAvance={soloAvance} />
      ) : (
        <M2Matriz m={m} metrica={metrica} soloAvance={soloAvance} busqueda={busqueda} />
      )}
    </div>
  );
}

// ---- Vista Por Partida: tabla agrupada; cada sub-partida se expande por obra ----
function M2PorPartida({ m, soloAvance }: { m: M2Reporte; soloAvance: boolean }) {
  const [expandidas, setExpandidas] = useState<Set<number>>(new Set());

  // Partidas/sub-partidas visibles según el filtro; subtotales recalculados.
  const partidas = useMemo(() => {
    const subOk = (s: M2Partida['subs'][number]) => !soloAvance || s.m2_semana > EPS;
    return m.partidas
      .map((p) => {
        const subs = p.subs.filter(subOk);
        return {
          ...p,
          subs,
          m2_semana: subs.reduce((a, s) => a + s.m2_semana, 0),
          m2_acumulado: subs.reduce((a, s) => a + s.m2_acumulado, 0),
        };
      })
      .filter((p) => p.subs.length > 0);
  }, [m.partidas, soloAvance]);

  const totSemana = partidas.reduce((a, p) => a + p.m2_semana, 0);
  const totAcum = partidas.reduce((a, p) => a + p.m2_acumulado, 0);

  function toggle(id: number) {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (partidas.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No hay producción de m² {soloAvance ? 'en la semana' : 'registrada'} para esta semana.
      </p>
    );
  }

  return (
    <div
      className="overflow-auto rounded-md border border-border"
      style={{ maxHeight: 'calc(100vh - 220px)' }}
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="sticky top-0 z-10 h-8 border-b border-border bg-zinc-100 px-3 text-left text-[11px] font-medium text-muted-foreground">
              Partida · Sub-partida · Obra
            </th>
            <th className="sticky top-0 z-10 h-8 w-32 border-b border-l border-border bg-zinc-100 px-3 text-right text-[11px] font-medium text-muted-foreground">
              m² Semana
            </th>
            <th className="sticky top-0 z-10 h-8 w-32 border-b border-l border-border bg-zinc-100 px-3 text-right text-[11px] font-medium text-muted-foreground">
              m² Acumulado
            </th>
          </tr>
        </thead>
        <tbody>
          {partidas.map((p) => (
            <PartidaBloque
              key={p.partida_id}
              p={p}
              m={m}
              soloAvance={soloAvance}
              expandidas={expandidas}
              toggle={toggle}
            />
          ))}
          <tr className="border-t-2 border-border bg-zinc-50 font-semibold">
            <td className="px-3 py-2">TOTAL</td>
            <td className="border-l border-border px-3 py-2 text-right tabular-nums">
              {fmt(totSemana)}
            </td>
            <td className="border-l border-border px-3 py-2 text-right tabular-nums">
              {fmt(totAcum)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PartidaBloque({
  p,
  m,
  soloAvance,
  expandidas,
  toggle,
}: {
  p: M2Partida;
  m: M2Reporte;
  soloAvance: boolean;
  expandidas: Set<number>;
  toggle: (id: number) => void;
}) {
  return (
    <>
      <tr className="bg-zinc-100/70 font-medium">
        <td className="px-3 py-1.5">
          <span className="font-mono text-xs text-muted-foreground">{p.partida_codigo}</span>{' '}
          {p.partida_nombre}
        </td>
        <td className="border-l border-border px-3 py-1.5 text-right tabular-nums">
          {fmt(p.m2_semana)}
        </td>
        <td className="border-l border-border px-3 py-1.5 text-right tabular-nums">
          {fmt(p.m2_acumulado)}
        </td>
      </tr>
      {p.subs.map((s) => {
        const abierta = expandidas.has(s.id);
        const obras = abierta ? obrasDeSub(m, s.id, soloAvance) : [];
        return (
          <FragmentoSub
            key={s.id}
            s={s}
            abierta={abierta}
            obras={obras}
            onToggle={() => toggle(s.id)}
          />
        );
      })}
    </>
  );
}

function FragmentoSub({
  s,
  abierta,
  obras,
  onToggle,
}: {
  s: M2Partida['subs'][number];
  abierta: boolean;
  obras: ReturnType<typeof obrasDeSub>;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-t border-border/60 hover:bg-muted/30">
        <td className="py-1 pr-3 pl-6">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center gap-1 text-left"
          >
            <span className="inline-flex w-3 text-muted-foreground">
              {abierta ? (
                <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" aria-hidden="true" />
              )}
            </span>
            <span>
              <span className="font-mono text-[11px] text-muted-foreground">{s.codigo}</span>{' '}
              {s.nombre}
              <span className="ml-1 text-[11px] text-muted-foreground">· S{s.sprint_numero}</span>
            </span>
          </button>
        </td>
        <td
          className={cn(
            'border-l border-border/60 px-3 py-1 text-right tabular-nums',
            s.m2_semana > EPS ? 'text-blue-700' : 'text-muted-foreground',
          )}
        >
          {s.m2_semana > EPS ? fmt(s.m2_semana) : '—'}
        </td>
        <td className="border-l border-border/60 px-3 py-1 text-right tabular-nums">
          {fmt(s.m2_acumulado)}
        </td>
      </tr>
      {abierta &&
        (obras.length === 0 ? (
          <tr className="bg-muted/20">
            <td className="py-1 pr-3 pl-14 text-[11px] text-muted-foreground" colSpan={3}>
              Sin obras con avance para esta sub-partida.
            </td>
          </tr>
        ) : (
          obras.map(({ o, c }) => (
            <tr key={o.codigo} className="bg-muted/20 text-[12px]">
              <td className="py-0.5 pr-3 pl-14">
                <span className="font-mono text-[11px]">{o.codigo}</span>
                <span className="ml-1 text-[11px] text-muted-foreground">
                  {o.tipo_casa ?? ''} · S{o.sprint_actual ?? '-'}
                </span>
              </td>
              <td
                className={cn(
                  'border-l border-border/60 px-3 py-0.5 text-right tabular-nums',
                  c && c.s > EPS ? 'text-blue-700' : 'text-muted-foreground',
                )}
              >
                {c && c.s > EPS ? fmt(c.s) : '—'}
              </td>
              <td className="border-l border-border/60 px-3 py-0.5 text-right tabular-nums">
                {fmt(c?.a)}
              </td>
            </tr>
          ))
        ))}
    </>
  );
}

// ---- Vista Matriz: sub-partidas (filas) × obras (columnas), m² por celda ----
function M2Matriz({
  m,
  metrica,
  soloAvance,
  busqueda,
}: {
  m: M2Reporte;
  metrica: Metrica;
  soloAvance: boolean;
  busqueda: string;
}) {
  const obras = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return m.obras
      .filter((o) => !soloAvance || o.m2_semana > EPS)
      .filter((o) => (q ? o.codigo.toLowerCase().includes(q) : true));
  }, [m.obras, soloAvance, busqueda]);

  const celda = (obra: string, sub: number) => m.celdas[`${obra}|${sub}`];

  // Sub-partidas visibles: con al menos una celda visible en las obras mostradas.
  const partidas = useMemo(() => {
    const visible = (subId: number) =>
      obras.some((o) => {
        const c = m.celdas[`${o.codigo}|${subId}`];
        return c != null && (soloAvance ? c.s > EPS : c.a > EPS || c.s > EPS);
      });
    return m.partidas
      .map((p) => ({ ...p, subs: p.subs.filter((s) => visible(s.id)) }))
      .filter((p) => p.subs.length > 0);
  }, [m.partidas, m.celdas, obras, soloAvance]);

  const thBase = 'bg-zinc-100 px-1 text-[11px] font-medium text-muted-foreground';
  const totalW = 260 + obras.length * 64;

  function celdaStyle(val: number, tuvoSemana: boolean): { txt: string; style: CSSProperties } {
    if (val <= EPS) return { txt: '·', style: { background: '#fafafa', color: '#d4d4d8' } };
    const linea: CSSProperties = tuvoSemana ? { boxShadow: 'inset 3px 0 0 #2563eb' } : {};
    return {
      txt: fmt(val),
      style: { background: 'rgba(59,130,246,0.08)', color: '#1e3a8a', ...linea },
    };
  }

  if (obras.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Ninguna obra con producción de m² {soloAvance ? 'en la semana' : ''}.
      </p>
    );
  }

  return (
    <div
      className="overflow-auto rounded-md border border-border"
      style={{ maxHeight: 'calc(100vh - 220px)' }}
    >
      <table className="table-fixed border-collapse" style={{ width: totalW }}>
        <colgroup>
          <col style={{ width: 260 }} />
          {obras.map((o) => (
            <col key={o.codigo} style={{ width: 64 }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th
              className={cn(thBase, 'sticky top-0 left-0 z-30 border-b border-border text-left')}
              style={{ height: 34 }}
            >
              Sub-partida
            </th>
            {obras.map((o) => (
              <th
                key={o.codigo}
                className={cn(
                  thBase,
                  'sticky top-0 z-20 overflow-hidden border-b border-l border-border align-bottom',
                )}
                title={`${o.codigo} · ${o.tipo_casa ?? ''} · S${o.sprint_actual ?? '-'} · sem ${fmt(o.m2_semana)} m² · acum ${fmt(o.m2_acumulado)} m²`}
              >
                <span className="block truncate font-mono text-[10px]">{o.codigo}</span>
                <span className="block truncate text-[9px] font-normal text-blue-700">
                  {fmt(metrica === 'semana' ? o.m2_semana : o.m2_acumulado)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {partidas.map((p) => (
            <MatrizPartida
              key={p.partida_id}
              p={p}
              obras={obras}
              metrica={metrica}
              celda={celda}
              celdaStyle={celdaStyle}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatrizPartida({
  p,
  obras,
  metrica,
  celda,
  celdaStyle,
}: {
  p: M2Partida;
  obras: M2Reporte['obras'];
  metrica: Metrica;
  celda: (obra: string, sub: number) => { s: number; a: number } | undefined;
  celdaStyle: (val: number, tuvoSemana: boolean) => { txt: string; style: CSSProperties };
}) {
  return (
    <>
      <tr>
        <td
          className="sticky left-0 z-10 border-y border-border bg-zinc-100 px-2 py-1 text-[11px] font-medium"
          colSpan={1 + obras.length}
        >
          {p.partida_codigo} — {p.partida_nombre}
          <span className="ml-2 text-muted-foreground">
            ({metrica === 'semana' ? fmt(p.m2_semana) : fmt(p.m2_acumulado)} m²)
          </span>
        </td>
      </tr>
      {p.subs.map((s) => (
        <tr key={s.id}>
          <td className="sticky left-0 z-10 truncate border-b border-border bg-white px-2 py-1 text-[11px]">
            <span className="font-mono text-[10px] text-muted-foreground">{s.codigo}</span>{' '}
            {s.nombre}
          </td>
          {obras.map((o) => {
            const c = celda(o.codigo, s.id);
            const val = c ? (metrica === 'semana' ? c.s : c.a) : 0;
            const info = celdaStyle(val, !!c && c.s > EPS);
            return (
              <td
                key={o.codigo}
                className="border-b border-l border-border px-1 py-1 text-right text-[10px] tabular-nums"
                style={info.style}
              >
                {info.txt}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
